import React, { useCallback, useEffect, useRef, useState, ChangeEvent } from "react";
import { Box, Button, Flex, FormControl, FormErrorMessage, Input, ModalBody, ModalFooter, Switch, Text, Select } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ClasswizeModal } from "@/components/ui/Modal";
import useStore, { ClasswizeState } from "@/stores/index";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { colours } from "@/utils/themeUtils";
import { fonts } from "@/theme/fonts";
import { Notice } from "@/components/Notice";
import { useClasswizeToast } from "@/components/ui/Toast";
import { useAccountSettingsQuery, useUpsertAccountSettingsMutation } from "@/query/accountSettingsQuery";
import { isChatFeatureEnabled } from "@/js/utils/chatUtils";
import { ACCOUNT_LOCALE_EN, ACCOUNT_LOCALE_ES, applyResolvedLanguageFromSession, normalizePreferredLocaleForSelect } from "@/i18n";
import { captureAccountSettingsSavedEvent } from "@/utils/posthogUtils";

const CHAT_DISPLAY_NAME_MAX_LENGTH = 80;

function applyChatDisplayNameToStore(chatDisplayName: string): void {
	useStore.setState((state) => ({
		chat: {
			...state.chat,
			displayName: {
				...state.chat.displayName,
				data: chatDisplayName,
				error: null,
				errorMsg: "",
				isSubmittingDisplayName: false,
			},
		},
	}));
}

interface Props {
	isOpen: boolean;
	/** Clears menu-driven open state and chat username prompt (same as Close). */
	onClose: () => void;
	/** True when chat opened without a username and triggered this modal */
	usernamePromptMode: boolean;
}

export function AccountSettingsModal(props: Props): JSX.Element {
	const { onClose, usernamePromptMode, isOpen } = props;
	const { t } = useTranslation();

	const userSessionStore = useUserSessionStore();
	const applianceId = userSessionStore.getActiveDevice() as string;
	const { errorToast } = useClasswizeToast();

	const [chatDisplayName, setChatDisplayName] = useState<string>(() => useStore.getState().chat.displayName.data ?? "");
	const [use24HourTime, setUse24HourTime] = useState<boolean>(false);
	const [preferredLocale, setPreferredLocale] = useState<string>(ACCOUNT_LOCALE_EN);

	const hydratedForOpenRef = useRef(false);
	const reportedFetchErrorRef = useRef(false);

	const settingsQuery = useAccountSettingsQuery(isOpen, applianceId);
	const upsertMutation = useUpsertAccountSettingsMutation(applianceId);

	const validateDisplayNameInput = useStore((state: ClasswizeState) => state.chat.validateDisplayNameInput);
	const chatFeatureEnabled = isChatFeatureEnabled();

	useEffect(() => {
		if (!isOpen) {
			hydratedForOpenRef.current = false;
			reportedFetchErrorRef.current = false;
			return;
		}

		if (settingsQuery.isError && !reportedFetchErrorRef.current) {
			reportedFetchErrorRef.current = true;
			errorToast({
				title: t("account.loadErrorTitle"),
				description: t("account.loadErrorDescription"),
				isClosable: true,
			});
		}

		if (!settingsQuery.isError) {
			reportedFetchErrorRef.current = false;
		}
	}, [isOpen, settingsQuery.isError, errorToast, t]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		if (hydratedForOpenRef.current) {
			return;
		}
		if (!settingsQuery.data) {
			return;
		}

		const d = settingsQuery.data;
		setChatDisplayName(d.chatDisplayName ?? "");
		setUse24HourTime(d.use24Hour);
		setPreferredLocale(normalizePreferredLocaleForSelect(d.preferredLocale));
		hydratedForOpenRef.current = true;
	}, [isOpen, settingsQuery.data]);

	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	const handleSave = useCallback((): void => {
		if (chatFeatureEnabled && !validateDisplayNameInput(chatDisplayName)) {
			return;
		}

		upsertMutation.mutate(
			{ use24Hour: use24HourTime, chatDisplayName: chatDisplayName.trim(), preferredLocale },
			{
				onSuccess: (result) => {
					const name = result.chatDisplayName ?? chatDisplayName.trim();
					applyChatDisplayNameToStore(name);
					setChatDisplayName(name);
					setUse24HourTime(result.use24Hour);
					captureAccountSettingsSavedEvent({
						teacherUsername: userSessionStore.getActiveUser() as string,
						applianceId,
						use24Hour: result.use24Hour,
						preferredLocale: result.preferredLocale ?? preferredLocale,
					});
					void applyResolvedLanguageFromSession(result);
					onClose();
				},
				onError: () => {
					errorToast({
						title: t("account.saveErrorTitle"),
						description: t("account.saveErrorDescription"),
						isClosable: true,
					});
				},
			}
		);
	}, [
		chatDisplayName,
		use24HourTime,
		preferredLocale,
		chatFeatureEnabled,
		validateDisplayNameInput,
		upsertMutation,
		onClose,
		errorToast,
		t,
		userSessionStore,
		applianceId,
	]);

	const inputsDisabled = settingsQuery.isLoading;
	const trimmedChatDisplayName = chatDisplayName.trim();
	const chatDisplayNameEmpty = chatFeatureEnabled && trimmedChatDisplayName.length === 0;
	const chatDisplayNameTooLong = chatFeatureEnabled && trimmedChatDisplayName.length > CHAT_DISPLAY_NAME_MAX_LENGTH;
	const chatDisplayNameInvalid = chatDisplayNameEmpty || chatDisplayNameTooLong;
	const chatDisplayNameErrorMsg = chatDisplayNameEmpty
		? t("account.chatDisplayNameRequired")
		: chatDisplayNameTooLong
			? t("account.chatDisplayNameTooLong", {
					count: trimmedChatDisplayName.length - CHAT_DISPLAY_NAME_MAX_LENGTH,
				})
			: "";
	const saveDisabledByChatDisplayName =
		chatFeatureEnabled && (trimmedChatDisplayName.length === 0 || trimmedChatDisplayName.length > CHAT_DISPLAY_NAME_MAX_LENGTH);
	const saveDisabled = settingsQuery.isLoading || saveDisabledByChatDisplayName || upsertMutation.isLoading;

	return (
		<ClasswizeModal
			id="account-settings-modal"
			isOpen={isOpen}
			onClose={handleClose}
			title={t("account.modalTitle")}
			size="xl"
			dataTestId="account-settings-modal"
		>
			<ModalBody>
				<Box>
					<Text mb={14} color={colours.navy[800]} fontSize="14px" lineHeight="1.5" fontWeight={400} fontFamily={fonts.body}>
						{t("account.signedInAs")}{" "}
						<Text as="span" wordBreak="break-word" overflowWrap="anywhere">
							{userSessionStore.getActiveUser()}
						</Text>
					</Text>

					{chatFeatureEnabled && usernamePromptMode && (
						<Box mb={4}>
							<Notice status="info">{t("account.chatUsernameNotice")}</Notice>
						</Box>
					)}

					{chatFeatureEnabled && (
						<FormControl mb={4} isInvalid={chatDisplayNameInvalid} isRequired>
							<Text
								as="label"
								htmlFor="account-chat-display-name"
								display="block"
								fontFamily={fonts.body}
								fontWeight={600}
								color={colours.navy[800]}
								fontSize="14px"
								mb={2}
							>
								{t("account.chatDisplayNameLabel")}
							</Text>
							<Input
								id="account-chat-display-name"
								value={chatDisplayName}
								onChange={(e: ChangeEvent<HTMLInputElement>) => setChatDisplayName(e.target.value)}
								placeholder={t("account.chatDisplayNamePlaceholder")}
								disabled={inputsDisabled}
								borderColor={colours.neutrals[100]}
								fontFamily={fonts.body}
								fontSize="14px"
								data-testid="chat-display-name-input"
							/>
							<FormErrorMessage fontFamily={fonts.body} fontSize="14px" mt={2}>
								{chatDisplayNameErrorMsg}
							</FormErrorMessage>
						</FormControl>
					)}

					<Box my={12} aria-hidden />

					<Flex alignItems="center" justify="space-between" width="100%" mb={10} gap={4}>
						<Text
							as="label"
							htmlFor="account-use-24-hour-time"
							fontFamily={fonts.body}
							fontWeight={600}
							color={colours.navy[800]}
							fontSize="14px"
							mb={0}
							cursor="pointer"
							flex="1"
						>
							{t("account.use24HourLabel")}
						</Text>
						<Switch
							id="account-use-24-hour-time"
							isChecked={use24HourTime}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setUse24HourTime(e.target.checked);
							}}
							isDisabled={inputsDisabled}
							data-testid="account-use-24-hour-time"
							flexShrink={0}
						/>
					</Flex>

					<FormControl mb={10}>
						<Text
							as="label"
							htmlFor="account-preferred-locale"
							display="block"
							fontFamily={fonts.body}
							fontWeight={600}
							color={colours.navy[800]}
							fontSize="14px"
							mb={2}
						>
							{t("account.preferredLanguageLabel")}
						</Text>
						<Select
							id="account-preferred-locale"
							value={preferredLocale}
							onChange={(e: ChangeEvent<HTMLSelectElement>) => setPreferredLocale(e.target.value)}
							disabled={inputsDisabled}
							borderColor={colours.neutrals[100]}
							fontFamily={fonts.body}
							fontSize="14px"
							data-testid="account-preferred-locale"
						>
							<option value={ACCOUNT_LOCALE_EN} data-testid="language-option-en">
								{t("account.languageEnglish")}
							</option>
							<option value={ACCOUNT_LOCALE_ES} data-testid="language-option-es">
								{t("account.languageSpanish")}
							</option>
						</Select>
					</FormControl>
				</Box>
			</ModalBody>
			<ModalFooter>
				<Button id="account-settings-close-footer" onClick={handleClose} variant="ghost" mr={2} data-testid="account-settings-close-footer">
					{t("account.close")}
				</Button>
				<Button
					id="account-settings-save"
					onClick={handleSave}
					fontFamily={fonts.buttons}
					isLoading={upsertMutation.isLoading}
					isDisabled={saveDisabled}
					data-testid="account-settings-save"
				>
					{t("account.save")}
				</Button>
			</ModalFooter>
		</ClasswizeModal>
	);
}
